/**
 * Auth provider abstraction for the HTTP layer (axios interceptors).
 * Allows tests to supply a token provider without loading MSAL; the app
 * registers the real implementation when AuthProvider mounts.
 */

export interface IAuthProviderForAxios {
  /** Return a bearer token for API requests. Throws on auth required (e.g. AUTHENTICATION_REQUIRED). */
  getAuthToken(): Promise<string>;
  /** Called when token is missing or refresh fails so the app can redirect to login. */
  onAuthRequired(): void | Promise<void>;
}

let authProvider: IAuthProviderForAxios | null = null;

const noOpProvider: IAuthProviderForAxios = {
  getAuthToken: () => Promise.resolve(''),
  onAuthRequired: () => {},
};

/**
 * Get the current auth provider. Returns a no-op (no token, no redirect) if none registered.
 * Used by axiosInstance so it never imports ScimAuthService or AuthProvider.
 */
export function getAuthProvider(): IAuthProviderForAxios {
  return authProvider ?? noOpProvider;
}

/**
 * Register the auth provider. Call once when the app has auth ready (e.g. in AuthProvider mount).
 * Tests can call this with a mock to avoid loading MSAL.
 */
export function setAuthProvider(provider: IAuthProviderForAxios | null): void {
  authProvider = provider;
}
